import React, { useEffect } from 'react';

// Extend the Window interface to include adsbygoogle
declare global {
  interface Window {
    adsbygoogle: any[];
  }
}

interface GoogleAdProps {
  adSlot: string;
  adFormat?: 'auto' | 'rectangle' | 'horizontal' | 'vertical';
  adStyle?: React.CSSProperties;
  className?: string;
  responsive?: boolean;
  isDarkTheme?: boolean;
}

const GoogleAd: React.FC<GoogleAdProps> = ({
  adSlot,
  adFormat = 'auto',
  adStyle = {},
  className = '',
  responsive = true,
  isDarkTheme = false
}) => {
  useEffect(() => {
    try {
      // Check if adsbygoogle is available
      if (typeof window !== 'undefined' && window.adsbygoogle) {
        window.adsbygoogle.push({});
      }
    } catch (error) {
      console.error('Google AdSense error:', error);
    }
  }, []);

  return (
    <div 
      className={`google-ad-container ${className} ${
        isDarkTheme ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
      } border rounded-lg p-4 my-6 flex items-center justify-center min-h-[120px]`}
      style={adStyle}
    >
      {process.env.NODE_ENV === 'development' ? (
        // Show placeholder in development
        <div className={`text-center py-8 px-4 w-full ${
          isDarkTheme ? 'text-gray-400' : 'text-gray-500'
        }`}>
          <div className="border-2 border-dashed border-current rounded-lg p-4">
            <p className="text-sm font-medium mb-2">Advertisement Placeholder</p>
            <p className="text-xs opacity-75">Slot: {adSlot}</p>
            <p className="text-xs opacity-75">Format: {adFormat}</p>
          </div>
        </div>
      ) : (
        // Show actual ad in production
        <ins
          className="adsbygoogle"
          style={{
            display: 'block',
            width: '100%',
            height: 'auto',
            ...adStyle
          }}
          data-ad-client={process.env.REACT_APP_GOOGLE_ADSENSE_CLIENT}
          data-ad-slot={adSlot}
          data-ad-format={responsive ? 'auto' : adFormat}
          data-full-width-responsive={responsive ? 'true' : 'false'}
        />
      )}
    </div>
  );
};

// Predefined ad components for common use cases
export const HeaderAd: React.FC<{ isDarkTheme?: boolean }> = ({ isDarkTheme = false }) => (
  <GoogleAd
    adSlot={process.env.REACT_APP_HEADER_AD_SLOT || "1234567890"}
    adFormat="horizontal"
    className="header-ad"
    isDarkTheme={isDarkTheme}
  />
);

export const SidebarAd: React.FC<{ isDarkTheme?: boolean }> = ({ isDarkTheme = false }) => (
  <GoogleAd
    adSlot={process.env.REACT_APP_SIDEBAR_AD_SLOT || "1234567891"}
    adFormat="rectangle"
    className="sidebar-ad"
    isDarkTheme={isDarkTheme}
  />
);

export const ContentAd: React.FC<{ isDarkTheme?: boolean }> = ({ isDarkTheme = false }) => (
  <GoogleAd
    adSlot={process.env.REACT_APP_CONTENT_AD_SLOT || "1234567892"}
    adFormat="auto"
    className="content-ad"
    isDarkTheme={isDarkTheme}
  />
);

export const FooterAd: React.FC<{ isDarkTheme?: boolean }> = ({ isDarkTheme = false }) => (
  <GoogleAd
    adSlot={process.env.REACT_APP_FOOTER_AD_SLOT || "1234567893"}
    adFormat="horizontal"
    className="footer-ad"
    isDarkTheme={isDarkTheme}
  />
);

export default GoogleAd;